import * as React from "react";
import styled from "@mui/material/styles/styled";
import { Button, Theme } from "@mui/material";
import { ClinicColors } from "../styles/clinicTheme";
import { MouseEventHandler } from "react";
import {
  marginLeftToMarginRightOnRtl,
  marginRightToMarginLeftOnRtl,
  onRtl,
} from "../rtl/rtlStyles";

//region [[ Styles ]]

const ClinicActionButtonView = styled(Button)(
  ({ lightmode, theme }: { lightmode: string; theme?: Theme }) => ({
    borderRadius: "82px",
    textTransform: "none",
    fontSize: "16px",
    minWidth: "160px",
    boxShadow: "none",
    color: lightmode === "true" ? theme.palette.primary.main : "#FFFFFF",
    background: lightmode === "true" ? "#FFFFFF" : theme.palette.primary.main,
    "&:hover": {
      boxShadow: "none",
    },
    "& svg": {
      fill: "currentColor",
    },
  }),
);

const ClinicInnerButtonView = styled(Button)({
  color: ClinicColors.primary,
  borderRadius: 4,
  textTransform: "none",
  fontSize: 14,
  minWidth: 50,
  boxShadow: "none",
  "&:hover": {
    boxShadow: "none",
  },
  "&:enabled": {
    backgroundColor: ClinicColors.tertiary,
  },
  "&:enabled:hover": {
    backgroundColor: ClinicColors.tertiary + "AA",
  },
  "& .MuiButton-startIcon": {
    ...marginRightToMarginLeftOnRtl(8),
    color: ClinicColors.primary,
  },
  "& svg": {
    fill: "currentColor",
    // stroke: "currentColor",
    color: "currentColor",
  },
  "& svg path": {
    // stroke: "currentColor",
  },
});

const ClinicWhiteButtonView = styled(Button)({
  color: ClinicColors.textMain,
  borderRadius: 4,
  textTransform: "none",
  fontSize: 14,
  minWidth: 50,
  boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.11);",
  "&:enabled": {
    backgroundColor: "#FFFFFF",
  },
  "&:enabled:hover": {
    color: ClinicColors.primary,
    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.11);",
  },
  "& .MuiButton-endIcon": {
    ...marginLeftToMarginRightOnRtl(8),
  },
});

const MethodButtonView = styled(Button)({
  padding: 20,
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 14,
  border: "1px solid #FFFFFF",
  color: ClinicColors.textMain,
  width: "100%",
  display: "flex",
  gap: 12,
  justifyContent: "start",
  alignItems: "center",
  textDecoration: "none",
  textTransform: "none",
  textAlign: "left",
  lineHeight: 1.4,
  ...onRtl({
    textAlign: "right",
  }),
  "&:hover": {
    border: "1px solid " + ClinicColors.borders,
    textDecoration: "none",
  },
  "& .method-icon": {
    flex: "0 0 39px",
  },
  "& h5": {
    fontWeight: 700,
    fontSize: 16,
    padding: 0,
    margin: "0 0 4px 0",
  },
});

const ClinicSmallActionButton = styled(Button)<{ lightmode?: boolean }>(
  ({ lightmode }: { lightmode?: boolean; theme?: Theme }) => ({
    color: lightmode ? ClinicColors.primary : "#FFFFFF",
    border: lightmode ? `1px solid ${ClinicColors.primary}` : "none",
    borderRadius: 4,
    textTransform: "none",
    fontSize: 14,
    minWidth: 50,
    padding: 8,
    lineHeight: 1,
    boxShadow: "none",
    "&:enabled": {
      backgroundColor: lightmode ? "#FFFFFF" : ClinicColors.primary,
    },
    "&:enabled:hover": {
      opacity: 0.9,
      boxShadow: "none",
    },
    "&:disabled": {
      border: "none",
    },
    "& .MuiButton-startIcon": {
      ...marginRightToMarginLeftOnRtl(8),
      //color: ClinicColors.primary,
    },
    "& .MuiButton-endIcon": {
      ...marginLeftToMarginRightOnRtl(8),
      //color: ClinicColors.primary,
    },
    "& svg": {
      fill: "currentColor",
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicActionButtonProps {
  className?: string;
  onClick: MouseEventHandler;
  label: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  style?: React.CSSProperties;
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  fullWidth?: boolean;
  lightMode?: boolean;
  subTitle?: React.ReactNode;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicActionButton = ({ ...props }: ClinicActionButtonProps) => {
  return (
    <ClinicActionButtonView
      className={`${props.className} clinic-action-button`}
      onClick={props.onClick}
      variant={props.variant || "contained"}
      startIcon={props.icon}
      endIcon={props.endIcon}
      disabled={props.disabled}
      style={props.style}
      color={props.color || "primary"}
      fullWidth={props.fullWidth}
      lightmode={props.lightMode && "true"}
    >
      {props.label}
    </ClinicActionButtonView>
  );
};

export const ClinicInnerButton = ({ ...props }: ClinicActionButtonProps) => {
  return (
    <ClinicInnerButtonView
      className={`${props.className} clinic-inner-button`}
      onClick={props.onClick}
      variant={props.variant || "contained"}
      startIcon={props.icon}
      endIcon={props.endIcon}
      disabled={props.disabled}
      style={props.style}
    >
      {props.label}
    </ClinicInnerButtonView>
  );
};

export const ClinicWhiteButton = ({ ...props }: ClinicActionButtonProps) => {
  return (
    <ClinicWhiteButtonView
      className={`${props.className} clinic-inner-button`}
      onClick={props.onClick}
      variant={props.variant || "contained"}
      startIcon={props.icon}
      endIcon={props.endIcon}
      disabled={props.disabled}
      style={props.style}
    >
      {props.label}
    </ClinicWhiteButtonView>
  );
};

export const MethodButton = ({ ...props }: ClinicActionButtonProps) => {
  return (
    <MethodButtonView className="method-button" onClick={props.onClick}>
      {props.icon}
      <div>
        <h5>{props.label}</h5>
        {props.subTitle}
      </div>
    </MethodButtonView>
  );
};

export const ClinicSmallButton = ({
  ...props
}: {
  lightMode?: boolean;
} & ClinicActionButtonProps) => {
  return (
    <ClinicSmallActionButton
      className={`${props.className} clinic-inner-button`}
      onClick={props.onClick}
      variant={props.variant || "contained"}
      startIcon={props.icon}
      endIcon={props.endIcon}
      disabled={props.disabled}
      style={props.style}
      lightmode={props.lightMode}
    >
      {props.label}
    </ClinicSmallActionButton>
  );
};
